import React, { useCallback, useEffect, useRef, } from 'react';
import classnames from 'classnames';
import { Portal } from '../portal';
import { getTabbableElements } from '../../helpers/get-tabbable-elements';
import './overlay.scss';
export const Overlay = ({ className, show = false, blur = 0, opacity = 100, fullscreen = false, animate = false, zIndex = 20, dialog = false, onClose, ariaLabel, ariaLabelledBy, ariaDescribedBy, initialFocusRef, restoreFocus = true, children, }) => {
    const containerRef = useRef(null);
    const previousFocusRef = useRef(null);
    const overlayClassName = classnames('overlay', className, {
        'overlay--show': show && !animate,
        'overlay--enter': show && animate,
        'overlay--leave': !show && animate,
    });
    const overlayShadowStyles = {
        background: `rgba(34, 43, 53, ${opacity / 100})`,
    };
    if (blur) {
        overlayShadowStyles.backdropFilter = `blur(${blur}px)`;
    }
    /* eslint-disable consistent-return */
    useEffect(() => {
        if (!dialog || !show) {
            return;
        }
        previousFocusRef.current = document.activeElement;
        const container = containerRef.current;
        const frame = requestAnimationFrame(() => {
            if (!container) {
                return;
            }
            if (initialFocusRef?.current) {
                initialFocusRef.current.focus();
                return;
            }
            const tabbable = getTabbableElements(container);
            if (tabbable.length > 0) {
                tabbable[0].focus();
            }
            else {
                container.focus();
            }
        });
        return () => {
            cancelAnimationFrame(frame);
            if (!restoreFocus) {
                return;
            }
            const prev = previousFocusRef.current;
            if (prev && typeof prev.focus === 'function') {
                prev.focus();
            }
        };
    }, [dialog, show, initialFocusRef, restoreFocus]);
    /* eslint-enable consistent-return */
    const handleKeyDown = useCallback((event) => {
        if (!dialog) {
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onClose?.();
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const tabbable = getTabbableElements(container);
        if (tabbable.length === 0) {
            event.preventDefault();
            return;
        }
        const first = tabbable[0];
        const last = tabbable[tabbable.length - 1];
        const active = document.activeElement;
        if (!active || !container.contains(active)) {
            event.preventDefault();
            first.focus();
            return;
        }
        if (active === container) {
            event.preventDefault();
            if (event.shiftKey) {
                last.focus();
            }
            else {
                first.focus();
            }
            return;
        }
        if (event.shiftKey) {
            if (active === first) {
                event.preventDefault();
                last.focus();
            }
        }
        else if (active === last) {
            event.preventDefault();
            first.focus();
        }
    }, [dialog, onClose]);
    const dialogProps = dialog
        ? {
            role: 'dialog',
            'aria-modal': true,
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledBy,
            'aria-describedby': ariaDescribedBy,
            tabIndex: -1,
            onKeyDown: handleKeyDown,
        }
        : {};
    const overlay = (<div ref={containerRef} className={overlayClassName} style={{ zIndex }} {...dialogProps}>
      <div className="overlay__shadow" style={overlayShadowStyles}/>
      <div className="overlay__content">{children}</div>
    </div>);
    return fullscreen ? <Portal>{overlay}</Portal> : overlay;
};
